/**
 * EditorToolbar - Toolbar component for TipTap editor
 * 
 * Provides formatting buttons matching the original Jodit toolbar:
 * source, bold, italic, ul, ol, eraser, font, fontsize, brush, paragraph,
 * customImage, table, link, align, undo, redo, hr, copyformat, fullsize, dots
 */

import React, { useState, useRef } from 'react';
import './TipTapEditor.css';

const ToolbarButton = ({ onClick, isActive, disabled, title, children }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={`toolbar-button ${isActive ? 'active' : ''}`}
    title={title}
  >
    {children}
  </button>
);

const ToolbarDivider = () => <span className="toolbar-divider" />;

const EditorToolbar = ({ editor, onImageUpload, onSourceToggle, isSourceMode }) => {
  const [showFontSizeDropdown, setShowFontSizeDropdown] = useState(false);
  const [showTableDropdown, setShowTableDropdown] = useState(false);
  const fileInputRef = useRef(null);

  if (!editor) {
    return null;
  }

  const fontSizes = ['12px', '14px', '16px', '18px', '20px', '24px', '28px', '32px', '36px', '48px'];

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (file && onImageUpload) {
      await onImageUpload(file, editor);
    }
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const insertTable = (rows, cols) => {
    editor.chain().focus().insertTable({ rows, cols, withHeaderRow: true }).run();
    setShowTableDropdown(false);
  };

  return (
    <div className="tiptap-toolbar">
      {/* Source toggle */}
      <ToolbarButton
        onClick={onSourceToggle}
        isActive={isSourceMode}
        title="Source code"
      >
        <span className="toolbar-icon">&lt;/&gt;</span>
      </ToolbarButton>

      <ToolbarDivider />

      {/* Text formatting */}
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBold().run()}
        isActive={editor.isActive('bold')}
        disabled={isSourceMode}
        title="Bold"
      >
        <strong>B</strong>
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleItalic().run()}
        isActive={editor.isActive('italic')}
        disabled={isSourceMode}
        title="Italic"
      >
        <em>I</em>
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        isActive={editor.isActive('underline')}
        disabled={isSourceMode}
        title="Underline"
      >
        <u>U</u>
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleStrike().run()}
        isActive={editor.isActive('strike')}
        disabled={isSourceMode}
        title="Strikethrough"
      >
        <s>S</s>
      </ToolbarButton>

      <ToolbarDivider />

      {/* Lists */}
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        isActive={editor.isActive('bulletList')}
        disabled={isSourceMode}
        title="Bullet list"
      >
        <span className="toolbar-icon">•≡</span>
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        isActive={editor.isActive('orderedList')}
        disabled={isSourceMode}
        title="Numbered list"
      >
        <span className="toolbar-icon">1.</span>
      </ToolbarButton>

      {/* Clear formatting */}
      <ToolbarButton
        onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}
        disabled={isSourceMode}
        title="Clear formatting"
      >
        <span className="toolbar-icon">⌫</span>
      </ToolbarButton>

      <ToolbarDivider />

      {/* Font size dropdown */}
      <div className="toolbar-dropdown-container">
        <ToolbarButton
          onClick={() => setShowFontSizeDropdown(!showFontSizeDropdown)}
          disabled={isSourceMode}
          title="Font size"
        >
          <span className="toolbar-icon">A↕</span>
        </ToolbarButton>
        {showFontSizeDropdown && (
          <div className="toolbar-dropdown">
            {fontSizes.map((size) => (
              <button
                key={size}
                className="toolbar-dropdown-item"
                onClick={() => {
                  editor.chain().focus().setFontSize(size).run();
                  setShowFontSizeDropdown(false);
                }}
              >
                {size}
              </button>
            ))}
          </div>
        )}
      </div>

      <ToolbarDivider />

      {/* Image */}
      <ToolbarButton
        onClick={handleImageClick}
        disabled={isSourceMode}
        title="Insert image"
      >
        <span className="toolbar-icon">Image</span>
      </ToolbarButton>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      {/* Table */}
      <div className="toolbar-dropdown-container">
        <ToolbarButton
          onClick={() => setShowTableDropdown(!showTableDropdown)}
          isActive={editor.isActive('table')}
          disabled={isSourceMode}
          title="Insert table"
        >
          <span className="toolbar-icon">▦</span>
        </ToolbarButton>
        {showTableDropdown && (
          <div className="toolbar-dropdown table-dropdown">
            <div className="table-grid">
              {[1, 2, 3, 4, 5].map((rows) =>
                [1, 2, 3, 4, 5].map((cols) => (
                  <button
                    key={`${rows}-${cols}`}
                    className="table-grid-cell"
                    onClick={() => insertTable(rows, cols)}
                    title={`${rows}×${cols} table`}
                  />
                ))
              )}
            </div>
            {editor.isActive('table') && (
              <>
                <ToolbarDivider />
                <button
                  className="toolbar-dropdown-item"
                  onClick={() => {
                    editor.chain().focus().addRowAfter().run();
                    setShowTableDropdown(false);
                  }}
                >
                  Add row
                </button>
                <button
                  className="toolbar-dropdown-item"
                  onClick={() => {
                    editor.chain().focus().addColumnAfter().run();
                    setShowTableDropdown(false);
                  }}
                >
                  Add column
                </button>
                <button
                  className="toolbar-dropdown-item"
                  onClick={() => {
                    editor.chain().focus().deleteTable().run();
                    setShowTableDropdown(false);
                  }}
                >
                  Delete table
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Link */}
      <ToolbarButton
        onClick={() => {
          const url = window.prompt('Enter URL:');
          if (url) {
            editor.chain().focus().setLink({ href: url }).run();
          }
        }}
        isActive={editor.isActive('link')}
        disabled={isSourceMode}
        title="Insert link"
      >
        <span className="toolbar-icon">Link</span>
      </ToolbarButton>

      <ToolbarDivider />

      {/* Alignment */}
      <ToolbarButton
        onClick={() => editor.chain().focus().setTextAlign('left').run()}
        isActive={editor.isActive({ textAlign: 'left' })}
        disabled={isSourceMode}
        title="Align left"
      >
        <span className="toolbar-icon">⫷</span>
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().setTextAlign('center').run()}
        isActive={editor.isActive({ textAlign: 'center' })}
        disabled={isSourceMode}
        title="Align center"
      >
        <span className="toolbar-icon">≡</span>
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().setTextAlign('right').run()}
        isActive={editor.isActive({ textAlign: 'right' })}
        disabled={isSourceMode}
        title="Align right"
      >
        <span className="toolbar-icon">⫸</span>
      </ToolbarButton>

      <ToolbarDivider />

      {/* Undo/Redo */}
      <ToolbarButton
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().undo() || isSourceMode}
        title="Undo"
      >
        <span className="toolbar-icon">↶</span>
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().redo() || isSourceMode}
        title="Redo"
      >
        <span className="toolbar-icon">↷</span>
      </ToolbarButton>

      <ToolbarDivider />

      {/* Horizontal rule */}
      <ToolbarButton
        onClick={() => editor.chain().focus().setHorizontalRule().run()}
        disabled={isSourceMode}
        title="Horizontal line"
      >
        <span className="toolbar-icon">―</span>
      </ToolbarButton>
    </div>
  );
};

export default EditorToolbar;
